import React, { useMemo } from 'react'

/**
 * TimelineRuler Component
 * Displays time markers along the timeline with appropriate granularity based on zoom
 * Shows current playhead position
 */
const TimelineRuler = ({
  duration,
  currentTime,
  zoom,
  onSeek,
  timeToPixels,
  scrollLeft = 0
}) => {
  // Calculate time interval based on zoom level
  const timeInterval = useMemo(() => {
    if (zoom <= 50) return 10 // Every 10 seconds at low zoom
    if (zoom <= 100) return 5 // Every 5 seconds at normal zoom
    if (zoom <= 200) return 2 // Every 2 seconds at high zoom
    return 1 // Every 1 second at very high zoom
  }, [zoom])

  // Generate time markers
  const timeMarkers = useMemo(() => {
    const markers = []
    const totalMarkers = Math.ceil(duration / timeInterval) + 1

    for (let i = 0; i < totalMarkers; i++) {
      const time = i * timeInterval
      if (time <= duration) {
        markers.push(time)
      }
    }

    return markers
  }, [duration, timeInterval])

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Handle click on ruler
  const handleRulerClick = (e) => {
    if (!onSeek) return

    const rect = e.currentTarget.getBoundingClientRect()
    const clickX = e.clientX - rect.left + scrollLeft
    const clickedTime = clickX / (timeToPixels(1, zoom))

    // Clamp to valid range
    const clampedTime = Math.max(0, Math.min(clickedTime, duration))
    onSeek(clampedTime)
  }

  const playheadPosition = timeToPixels(currentTime, zoom)

  return (
    <div className="relative h-10 bg-black/30 border-b border-white/10 overflow-x-hidden">
      {/* Clickable area */}
      <div
        className="absolute inset-0 cursor-pointer"
        onClick={handleRulerClick}
      />

      {/* Time markers - translated based on scroll position */}
      <div
        className="relative h-full"
        style={{
          transform: `translateX(-${scrollLeft}px)`,
          willChange: 'transform'
        }}
      >
        {timeMarkers.map((time) => {
          const x = timeToPixels(time, zoom)

          return (
            <div
              key={time}
              className="absolute top-0 h-full"
              style={{ left: `${x}px` }}
            >
              {/* Tick mark */}
              <div className="h-full w-px bg-white/20" />

              {/* Time label */}
              <div className="absolute top-1 left-1 text-xs text-white/60 whitespace-nowrap select-none">
                {formatTime(time)}
              </div>
            </div>
          )
        })}
      </div>

      {/* Playhead indicator */}
      {currentTime >= 0 && currentTime <= duration && (
        <div
          className="absolute top-0 h-full w-0.5 bg-red-500 pointer-events-none z-10"
          style={{
            left: `${playheadPosition - scrollLeft}px`,
            boxShadow: '0 0 8px rgba(239, 68, 68, 0.8)'
          }}
        >
          {/* Playhead triangle */}
          <div
            className="absolute top-0 left-1/2 -translate-x-1/2 w-0 h-0"
            style={{
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderTop: '8px solid #ef4444'
            }}
          />
        </div>
      )}
    </div>
  )
}

export default TimelineRuler
